import { useSearchParams } from "react-router-dom";
import { AcceptedTermsCard } from "@/components/AcceptedTermsCard";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Copy, Download, ExternalLink, Loader2, Trash2, Link as LinkIcon, GraduationCap, Users, RefreshCw, Share2, Mail, ShieldCheck, AlertTriangle, PencilLine, Clock, CheckCircle2, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { buildStudentIntakeCsv, downloadCsv } from "@/lib/studentIntakeCsv";
import { SubmissionDetailDialog } from "./SubmissionDetailDialog";
import { ShareSubmissionsDialog } from "./ShareSubmissionsDialog";
import { ActiveSharesCard } from "./ActiveSharesCard";
import { SensitiveDocsPanel } from "./SensitiveDocsPanel";
import { EmailStudentsDialog, type EmailRecipient, type TemplateKey } from "./EmailStudentsDialog";
import { RequestFixDialog, type CreatedFixLink, type FixTargetSubmission } from "./RequestFixDialog";
import { MoveSubmissionDialog } from "./MoveSubmissionDialog";
import { ExpectedParticipantsCard } from "./ExpectedParticipantsCard";
import { NeedsReviewCard } from "./NeedsReviewCard";
import { IntakeLinkGuardDialog, isRiskyIntakeLead } from "./IntakeLinkGuardDialog";
import { fieldLabels } from "@/lib/intakeFixFields";
import { hasAllergy } from "@/lib/allergyUtils";
import { needsPassportAttention, verdictLabel, needsIdPhotoAttention, idPhotoVerdictLabel } from "@/lib/passportDocCheck";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";


const APP_URL = (import.meta.env.VITE_PUBLIC_APP_URL || "https://dashboard.creativeinvestments.africa").replace(/\/$/, "");

interface Props { leadId: string; leadTitle: string; }

type IntakeType = "student" | "client";

export function StudentIntakeTab({ leadId, leadTitle }: Props) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<any | null>(null);
  const [confirmChange, setConfirmChange] = useState<IntakeType | null>(null);
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTargets, setEmailTargets] = useState<EmailRecipient[]>([]);
  const [emailTemplate, setEmailTemplate] = useState<TemplateKey>("blank");
  const [fixOpen, setFixOpen] = useState(false);
  const [fixTargets, setFixTargets] = useState<FixTargetSubmission[]>([]);
  const [fixPreset, setFixPreset] = useState<string[]>([]);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [moveTarget, setMoveTarget] = useState<{ id: string; full_name: string } | null>(null);
  const [guard, setGuard] = useState<{ label: string; run: () => void } | null>(null);


  const leadQ = useQuery({
    queryKey: ["student-intake-lead", leadId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("leads")
        .select("id, title, client_name, status, start_date, end_date, group_reference, pax_count, student_intake_token, student_intake_open, intake_type")
        .eq("id", leadId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const subsQ = useQuery({
    queryKey: ["student-intake-subs", leadId],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("student_intake_submissions").select("*").eq("lead_id", leadId).order("submitted_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Deep link from notifications: ?submissionId= auto-opens the detail dialog
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkId = searchParams.get("submissionId");
  useEffect(() => {
    if (!deepLinkId || !subsQ.data) return;
    const match = (subsQ.data as any[]).find((s) => s.id === deepLinkId);
    if (match) setSelected(match);
    const next = new URLSearchParams(searchParams);
    next.delete("submissionId");
    setSearchParams(next, { replace: true });
  }, [deepLinkId, subsQ.data]);

  // Same person also captured on another lead (wrong intake link used)
  const dupQ = useQuery({
    queryKey: ["student-intake-dupes", leadId, (subsQ.data as any[])?.length || 0],
    enabled: !!(subsQ.data as any[])?.length,
    queryFn: async () => {
      const emails = ((subsQ.data as any[]) || [])
        .map((s) => (s.email || "").trim().toLowerCase())
        .filter(Boolean);
      if (!emails.length) return [];
      const { data, error } = await (supabase as any)
        .from("student_intake_submissions")
        .select("id, lead_id, email, full_name, leads:lead_id (title)")
        .neq("lead_id", leadId)
        .in("email", emails);
      if (error) throw error;
      return data || [];
    },
  });

  function duplicateOf(sub: any): string | null {
    const email = (sub.email || "").trim().toLowerCase();
    if (!email) return null;
    const hit = ((dupQ.data as any[]) || []).find((d) => (d.email || "").trim().toLowerCase() === email);
    return hit ? (hit.leads?.title || "another programme") : null;
  }

  const fixQ = useQuery({
    queryKey: ["intake-fix-requests", leadId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("intake_fix_requests")
        .select("id, submission_id, token, status, requested_fields, note, expires_at, sent_at, send_count, completed_at, created_at")
        .eq("lead_id", leadId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  function latestFix(submissionId: string) {
    return ((fixQ.data as any[]) || []).find((r) => r.submission_id === submissionId) || null;
  }

  const generateMut = useMutation({
    mutationFn: async (intakeType: IntakeType) => {
      const { data, error } = await (supabase as any).rpc("ensure_student_intake_token", { _lead_id: leadId, _intake_type: intakeType });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["student-intake-lead", leadId] }); toast.success("Intake link generated"); },
    onError: (e: any) => toast.error(e?.message || "Failed to generate link"),
  });

  const changeTypeMut = useMutation({
    mutationFn: async (intakeType: IntakeType) => {
      const { error: updErr } = await (supabase as any).from("leads").update({ intake_type: intakeType }).eq("id", leadId);
      if (updErr) throw updErr;
      const { data, error } = await (supabase as any).rpc("rotate_student_intake_token", { _lead_id: leadId });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["student-intake-lead", leadId] });
      toast.success("Intake type updated — a new link has been generated");
      setConfirmChange(null);
    },
    onError: (e: any) => toast.error(e?.message || "Failed to change intake type"),
  });

  const toggleMut = useMutation({
    mutationFn: async (open: boolean) => {
      const { error } = await (supabase as any).from("leads").update({ student_intake_open: open }).eq("id", leadId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["student-intake-lead", leadId] }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any).from("student_intake_submissions").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["student-intake-subs", leadId] }); toast.success("Submission deleted"); },
  });

  const token = leadQ.data?.student_intake_token;
  const intakeType: IntakeType = (leadQ.data?.intake_type as IntakeType) || "student";
  const url = token ? `${APP_URL}/${intakeType === "client" ? "client-intake" : "student-intake"}/${token}` : "";
  const isClient = intakeType === "client";

  function copy() { navigator.clipboard.writeText(url); toast.success("Link copied"); }

  /** Any action that puts this programme's intake link in front of a person goes through the guard. */
  function guarded(label: string, run: () => void) {
    setGuard({ label, run });
  }

  function exportCsv() {
    const rows = subsQ.data || [];
    if (rows.length === 0) { toast.error("No submissions to export"); return; }
    const csv = buildStudentIntakeCsv(rows as any);
    const slug = leadTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    downloadCsv(`${slug}-${intakeType}-intake-${dateStr}.csv`, csv);
  }

  function toRecipients(rows: any[]): EmailRecipient[] {
    return rows.map((s) => ({ id: s.id, full_name: s.full_name, preferred_name: s.preferred_name, email: s.email }));
  }

  function toFixTargets(rows: any[]): FixTargetSubmission[] {
    return rows.map((s) => ({ id: s.id, full_name: s.full_name, preferred_name: s.preferred_name, email: s.email }));
  }

  function openFix(rows: any[], preset: string[] = []) {
    if (!rows.length) { toast.error("No submissions selected"); return; }
    setFixTargets(toFixTargets(rows));
    setFixPreset(preset);
    setFixOpen(true);
  }

  function afterFixCreated(links: CreatedFixLink[]) {
    setEmailTargets(links.map((l) => ({
      id: l.submission.id,
      full_name: l.submission.full_name,
      preferred_name: l.submission.preferred_name,
      email: l.submission.email ?? null,
      fixUrl: l.url,
    })));
    setEmailTemplate("correction");
    setEmailOpen(true);
  }

  async function markInvitesSent(submissionIds: string[]) {
    const rows = ((fixQ.data as any[]) || []);
    const targets = emailTargets.filter((r) => submissionIds.includes(r.id) && r.fixUrl);
    if (!targets.length) return;
    for (const t of targets) {
      const req = rows.find((r) => r.submission_id === t.id && r.status === "open")
        || { id: null, send_count: 0 };
      if (!req.id) continue;
      // eslint-disable-next-line no-await-in-loop
      await (supabase as any)
        .from("intake_fix_requests")
        .update({ sent_at: new Date().toISOString(), send_count: (req.send_count || 0) + 1 })
        .eq("id", req.id);
    }
    qc.invalidateQueries({ queryKey: ["intake-fix-requests", leadId] });
  }

  function openEmail(rows: any[], template: TemplateKey = "blank") {
    if (!rows.length) { toast.error("No submissions selected"); return; }
    setEmailTargets(toRecipients(rows));
    setEmailTemplate(template);
    setEmailOpen(true);
  }

  async function verifyDoc(id: string, docKind: "passport" | "id_photo" = "passport") {
    setVerifyingId(id);
    try {
      const { data, error } = await supabase.functions.invoke("verify-intake-document", {
        body: { submission_id: id, doc_kind: docKind },
      });
      if (error) throw new Error(error.message);
      if ((data as any)?.error) throw new Error((data as any).error);
      const verdict = (data as any)?.verdict;
      qc.invalidateQueries({ queryKey: ["student-intake-subs", leadId] });
      if (docKind === "id_photo") {
        if (verdict === "portrait_photo") toast.success("ID photo looks like a valid portrait");
        else toast.warning(`ID photo flagged: ${idPhotoVerdictLabel(verdict)}`);
      } else if (verdict === "passport_page") {
        toast.success("Looks like a valid passport page");
      } else {
        toast.warning(`Flagged: ${verdictLabel(verdict)}`);
      }
    } catch (e: any) {
      toast.error(e?.message || "Document check failed");
    } finally {
      setVerifyingId(null);
    }
  }

  const verifyPassport = (id: string) => verifyDoc(id, "passport");

  async function verifyAll() {
    const rows = (subsQ.data as any[]) || [];
    const targets = rows.filter((s) => s.passport_photo_path || s.id_photo_path);
    if (!targets.length) { toast.error("No documents to check"); return; }
    toast.info(`Checking ${targets.length} submission${targets.length === 1 ? "" : "s"}…`);
    for (const s of targets) {
      // eslint-disable-next-line no-await-in-loop
      if (s.passport_photo_path) await verifyDoc(s.id, "passport");
      // eslint-disable-next-line no-await-in-loop
      if (s.id_photo_path) await verifyDoc(s.id, "id_photo");
    }
  }




  return (
    <div className="space-y-4">
      <AcceptedTermsCard leadId={leadId} />
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <LinkIcon className="h-4 w-4" /> Intake link
            {token && (
              <Badge variant="secondary" className="ml-2 uppercase text-[10px] tracking-wide">
                {isClient ? "Client intake" : "Student intake"}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!token ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Choose the intake type for this lead. The link is generated once you pick.
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <button
                  type="button"
                  disabled={generateMut.isPending}
                  onClick={() => generateMut.mutate("student")}
                  className="text-left border rounded-lg p-4 hover:border-primary transition-colors disabled:opacity-50"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <GraduationCap className="h-4 w-4 text-primary" />
                    <span className="font-medium">Student intake</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    For Kurus English language students. Includes course selection, ID photo and student-programme terms.
                  </p>
                </button>
                <button
                  type="button"
                  disabled={generateMut.isPending}
                  onClick={() => generateMut.mutate("client")}
                  className="text-left border rounded-lg p-4 hover:border-primary transition-colors disabled:opacity-50"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="h-4 w-4 text-primary" />
                    <span className="font-medium">Client intake</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Generic guest intake, branded to this lead's brand (Creative Travel Africa or Coffeebeans). No course, no student ID photo.
                  </p>
                </button>
              </div>
              {generateMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <Input readOnly value={url} className="font-mono text-xs" />
                <Button variant="outline" size="icon" onClick={() => guarded("Copy link", copy)}><Copy className="h-4 w-4" /></Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => guarded("Open form", () => window.open(url, "_blank", "noreferrer"))}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
              {isRiskyIntakeLead(leadQ.data) && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> This programme has ended or is no longer active — double-check
                  before sending this link to anyone.
                </p>
              )}
              <div className="flex items-center gap-3">
                <Switch
                  id="intake-open"
                  checked={leadQ.data?.student_intake_open ?? true}
                  onCheckedChange={(v) => toggleMut.mutate(v)}
                />
                <Label htmlFor="intake-open" className="cursor-pointer">
                  {leadQ.data?.student_intake_open ? "Accepting submissions" : "Closed"}
                </Label>
              </div>
              <p className="text-xs text-muted-foreground">
                {isClient
                  ? "Share this link with each guest — every guest fills it in independently."
                  : "Share this single link with the whole cohort — each student fills it independently."}
              </p>
              <div className="pt-2 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmChange(isClient ? "student" : "client")}
                  className="text-xs text-muted-foreground"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Switch to {isClient ? "Student intake" : "Client intake"}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <ExpectedParticipantsCard leadId={leadId} submissions={(subsQ.data as any[]) || []} />

      <NeedsReviewCard
        leadId={leadId}
        submissions={(subsQ.data as any[]) || []}
        duplicateLabel={(s) => duplicateOf(s)}
        onMove={(s) => setMoveTarget({ id: s.id, full_name: s.full_name })}
      />



      <SensitiveDocsPanel leadId={leadId} />

      <ActiveSharesCard leadId={leadId} />

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Submissions ({subsQ.data?.length || 0})</CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const rows = (subsQ.data as any[]) || [];
                const target = checkedIds.length ? rows.filter((s) => checkedIds.includes(s.id)) : rows;
                openEmail(target);
              }}
              disabled={!subsQ.data?.length}
            >
              <Mail className="h-4 w-4 mr-1" /> Email{checkedIds.length ? ` (${checkedIds.length})` : " all"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const rows = (subsQ.data as any[]) || [];
                openFix(checkedIds.length ? rows.filter((s) => checkedIds.includes(s.id)) : []);
              }}
              disabled={!checkedIds.length}
            >
              <PencilLine className="h-4 w-4 mr-1" /> Request correction{checkedIds.length ? ` (${checkedIds.length})` : ""}
            </Button>
            <Button variant="outline" size="sm" onClick={verifyAll} disabled={!subsQ.data?.length || !!verifyingId}>
              {verifyingId ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />} Check documents
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShareOpen(true)} disabled={!subsQ.data?.length}>
              <Share2 className="h-4 w-4 mr-1" /> Share{checkedIds.length ? ` (${checkedIds.length})` : ""}
            </Button>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!subsQ.data?.length}>
              <Download className="h-4 w-4 mr-1" /> Export CSV
            </Button>
          </div>

        </CardHeader>
        <CardContent>
          {subsQ.isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : !subsQ.data?.length ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No submissions yet. Share the link above with the cohort.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      <Checkbox
                        checked={checkedIds.length > 0 && checkedIds.length === (subsQ.data as any[]).length}
                        onCheckedChange={(c) => setCheckedIds(c ? (subsQ.data as any[]).map((s) => s.id) : [])}
                        aria-label="Select all submissions"
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    {!isClient && <TableHead>Course</TableHead>}
                    <TableHead>WhatsApp</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Passport</TableHead>
                    <TableHead>Diet/Allergy</TableHead>
                    <TableHead>Passport doc</TableHead>
                    {!isClient && <TableHead>ID photo</TableHead>}
                    <TableHead>Correction</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead></TableHead>

                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(subsQ.data as any[]).map((s) => (
                    <TableRow key={s.id} className="cursor-pointer" onClick={() => setSelected(s)}>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={checkedIds.includes(s.id)}
                          onCheckedChange={(c) => setCheckedIds((prev) => (c ? [...prev, s.id] : prev.filter((i) => i !== s.id)))}
                          aria-label={`Select ${s.full_name}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {s.full_name}
                        {(() => {
                          const dup = duplicateOf(s);
                          return dup ? (
                            <Badge
                              variant="outline"
                              className="ml-2 text-amber-600 border-amber-500 font-normal"
                              title={`Also captured on: ${dup}`}
                            >
                              <AlertTriangle className="h-3 w-3 mr-1" /> Also on {dup}
                            </Badge>
                          ) : null;
                        })()}
                      </TableCell>
                      {!isClient && <TableCell className="text-xs">{s.course || "—"}</TableCell>}
                      <TableCell className="text-xs">{s.whatsapp_e164}</TableCell>
                      <TableCell className="text-xs">{s.email}</TableCell>
                      <TableCell className="text-xs">{s.passport_number}</TableCell>
                      <TableCell className="text-xs">
                        {(s.dietary_tags || []).slice(0, 2).map((t: string) => <Badge key={t} variant="secondary" className="mr-1">{t}</Badge>)}
                        {hasAllergy(s.allergies) && <Badge variant="outline" className="text-destructive border-destructive">Allergy</Badge>}
                      </TableCell>
                      <TableCell className="text-xs">
                        {!s.passport_doc_verdict ? (
                          <span className="text-muted-foreground">Not checked</span>
                        ) : needsPassportAttention(s) ? (
                          <Badge variant="outline" className="text-destructive border-destructive">
                            <AlertTriangle className="h-3 w-3 mr-1" /> {verdictLabel(s.passport_doc_verdict)}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <ShieldCheck className="h-3 w-3 mr-1" /> {s.passport_doc_verified_by ? "Confirmed" : "Passport OK"}
                          </Badge>
                        )}
                      </TableCell>
                      {!isClient && (
                        <TableCell className="text-xs">
                          {!s.id_doc_verdict ? (
                            <span className="text-muted-foreground">Not checked</span>
                          ) : needsIdPhotoAttention(s) ? (
                            <Badge variant="outline" className="text-destructive border-destructive">
                              <AlertTriangle className="h-3 w-3 mr-1" /> {idPhotoVerdictLabel(s.id_doc_verdict)}
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              <ShieldCheck className="h-3 w-3 mr-1" /> {s.id_doc_verified_by ? "Confirmed" : "Photo OK"}
                            </Badge>
                          )}
                        </TableCell>
                      )}
                      <TableCell className="text-xs">
                        {(() => {
                          const fx = latestFix(s.id);
                          if (!fx) return <span className="text-muted-foreground">—</span>;
                          if (fx.status === "open") {
                            return (
                              <Badge variant="outline" className="text-amber-600 border-amber-500" title={fieldLabels(fx.requested_fields).join(", ")}>
                                <Clock className="h-3 w-3 mr-1" /> {fx.sent_at ? "Awaiting student" : "Link created"}
                              </Badge>
                            );
                          }
                          if (fx.status === "completed") {
                            return (
                              <Badge variant="secondary" title={fieldLabels(fx.completed_fields || fx.requested_fields).join(", ")}>
                                <CheckCircle2 className="h-3 w-3 mr-1" /> Corrected
                              </Badge>
                            );
                          }
                          return <span className="text-muted-foreground">Cancelled</span>;
                        })()}
                      </TableCell>
                      <TableCell className="text-xs">{new Date(s.submitted_at).toLocaleDateString()}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center">
                          {(() => {
                            const flagged = [
                              ...(needsPassportAttention(s) ? ["passport_photo"] : []),
                              ...(needsIdPhotoAttention(s) ? ["id_photo"] : []),
                            ];
                            return flagged.length ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className="mr-1"
                                title="Request a correction for the flagged document(s)"
                                onClick={() => openFix([s], flagged)}
                              ><PencilLine className="h-4 w-4 mr-1" /> Fix</Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Request a correction"
                                onClick={() => openFix([s], [])}
                              ><PencilLine className="h-4 w-4" /></Button>
                            );
                          })()}
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Email this student"
                            onClick={() => openEmail([s], "blank")}
                          ><Mail className="h-4 w-4" /></Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            title="Move to another programme"
                            onClick={() => setMoveTarget({ id: s.id, full_name: s.full_name })}
                          ><ArrowRightLeft className="h-4 w-4" /></Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            title="Check passport document"
                            disabled={verifyingId === s.id || !s.passport_photo_path}
                            onClick={() => verifyPassport(s.id)}
                          >
                            {verifyingId === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                          </Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { if (confirm("Delete this submission?")) deleteMut.mutate(s.id); }}

                          ><Trash2 className="h-4 w-4 text-destructive" /></Button>
                        </div>
                      </TableCell>

                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <SubmissionDetailDialog
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
        submission={selected}
        onEmail={(s) => openEmail([s], "blank")}
        onVerify={(id, kind) => { void verifyDoc(id, kind || "passport"); }}
        onRequestFix={(sub, preset) => openFix([sub], preset)}
        fixRequest={selected ? latestFix(selected.id) : null}
        verifying={!!selected && verifyingId === selected.id}
      />

      <EmailStudentsDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        leadId={leadId}
        recipients={emailTargets}
        intakeUrl={url}
        initialTemplate={emailTemplate}
        onSent={(ids) => { void markInvitesSent(ids); }}
      />

      <IntakeLinkGuardDialog
        open={!!guard}
        onOpenChange={(v) => !v && setGuard(null)}
        lead={leadQ.data}
        actionLabel={guard?.label || "Continue"}
        onConfirm={() => {
          const run = guard?.run;
          setGuard(null);
          run?.();
        }}
      />

      <MoveSubmissionDialog
        open={!!moveTarget}
        onOpenChange={(v) => !v && setMoveTarget(null)}
        leadId={leadId}
        submission={moveTarget}
        onMoved={() => {
          qc.invalidateQueries({ queryKey: ["student-intake-subs", leadId] });
          qc.invalidateQueries({ queryKey: ["student-intake-dupes", leadId] });
        }}
      />


      <RequestFixDialog
        open={fixOpen}
        onOpenChange={setFixOpen}
        leadId={leadId}
        submissions={fixTargets}
        presetKeys={fixPreset}
        onCreated={afterFixCreated}
      />


      <ShareSubmissionsDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        leadId={leadId}
        leadTitle={leadQ.data?.title || leadTitle}
        submissions={((subsQ.data as any[]) || []).map((s) => ({
          id: s.id,
          full_name: s.full_name,
          flag_unexpected: s.flag_unexpected,
          flag_duplicate_of: s.flag_duplicate_of,
          review_status: s.review_status,
        }))}
        preselectedIds={checkedIds.length ? checkedIds : ((subsQ.data as any[]) || []).map((s) => s.id)}
      />


      <AlertDialog open={!!confirmChange} onOpenChange={(v) => !v && setConfirmChange(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch intake type?</AlertDialogTitle>
            <AlertDialogDescription>
              This lead will switch to <strong>{confirmChange === "client" ? "Client intake" : "Student intake"}</strong>.
              A new intake link will be generated and the previous link will stop working immediately.
              Existing submissions remain untouched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={changeTypeMut.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={changeTypeMut.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (confirmChange) changeTypeMut.mutate(confirmChange);
              }}
            >
              {changeTypeMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Switch &amp; generate new link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}